<script setup lang="ts">
import {
  ArrowDownFromLine,
  CircleHelp,
  Cog,
  LogOut,
  MousePointerClick,
  MousePointer2,
  LayoutTemplate,
} from "@lucide/vue";
import { computed, inject, ref } from "vue";
import type {
  PopupEmbedSettings,
  PopupFrequencyIntervalUnit,
  PopupFrequencySettings,
  PopupFrequencyShowAgain,
  PopupFrequencyStopShowing,
  PopupShowAgainMode,
  PopupStopShowingAfterClosedBehavior,
  PopupTriggerConditions,
  PopupTriggersSettings,
} from "@aswin.dev/types";
import {
  createDefaultTriggerConditions,
  normalizePopupFrequencySettings,
  normalizeTriggerConditions,
  toPopupTriggersEmbedJson,
} from "@aswin.dev/types";
import { useI18n } from "../composables/useI18n";
import { EDITOR_KEY } from "../keys";
import type { BaseEditorReturn } from "../composables/useEditorCore";
import { resolvePopupEmbed } from "../utils/resolvePopupEmbed";
import PopupTriggerConditionDetails from "./PopupTriggerConditionDetails.vue";
import { hustlepopsHelpUrl } from "../constants/hustlepopsHelp";

const editor = inject(EDITOR_KEY) as BaseEditorReturn | null;
if (!editor) {
  throw new Error("PopupDisplayRulesView requires EDITOR_KEY");
}

const { t } = useI18n();

const rulesTab = ref<"trigger" | "frequency">("trigger");

const rulesTabs = computed(() => [
  { id: "trigger" as const, label: t.popupDisplayRules.tabTrigger },
  { id: "frequency" as const, label: t.popupDisplayRules.tabFrequency },
]);

const popup = computed(() => resolvePopupEmbed(editor!.content.value.settings));

function commit(next: PopupEmbedSettings): void {
  editor!.updateSettings({ popup: next });
}

function patchTrigger<K extends keyof PopupTriggersSettings>(
  key: K,
  patch: Partial<PopupTriggersSettings[K]>,
): void {
  const cur = popup.value;
  commit({
    ...cur,
    triggers: {
      ...cur.triggers,
      [key]: { ...cur.triggers[key], ...patch },
    },
  });
}

const triggerDefs = computed(() => [
  {
    key: "onLanding" as const,
    icon: LayoutTemplate,
    title: t.popupDisplayRules.triggerOnLandingTitle,
    desc: t.popupDisplayRules.triggerOnLandingDesc,
    kind: "landing" as const,
  },
  {
    key: "onExit" as const,
    icon: LogOut,
    title: t.popupDisplayRules.triggerOnExitTitle,
    desc: t.popupDisplayRules.triggerOnExitDesc,
    kind: "exit" as const,
  },
  {
    key: "onScroll" as const,
    icon: ArrowDownFromLine,
    title: t.popupDisplayRules.triggerOnScrollTitle,
    desc: t.popupDisplayRules.triggerOnScrollDesc,
    kind: "scroll" as const,
  },
  {
    key: "onClick" as const,
    icon: MousePointerClick,
    title: t.popupDisplayRules.triggerOnClickTitle,
    desc: t.popupDisplayRules.triggerOnClickDesc,
    kind: "click" as const,
  },
  {
    key: "onHover" as const,
    icon: MousePointer2,
    title: t.popupDisplayRules.triggerOnHoverTitle,
    desc: t.popupDisplayRules.triggerOnHoverDesc,
    kind: "hover" as const,
  },
  {
    key: "onCustomEvent" as const,
    icon: Cog,
    title: t.popupDisplayRules.triggerOnCustomTitle,
    desc: t.popupDisplayRules.triggerOnCustomDesc,
    kind: "custom" as const,
  },
]);

const debugTriggersOpen = ref(false);
const debugCopyDone = ref(false);

function triggerSlotConditions<K extends keyof PopupTriggersSettings>(
  key: K,
): PopupTriggerConditions {
  return normalizeTriggerConditions(
    popup.value.triggers[key].triggerConditions,
  );
}

function patchConditions<K extends keyof PopupTriggersSettings>(
  key: K,
  partial: Partial<PopupTriggerConditions>,
): void {
  const prev = triggerSlotConditions(key);
  patchTrigger(key, {
    triggerConditions: { ...prev, ...partial },
  } as Partial<PopupTriggersSettings[K]>);
}

function setConditionsPackEnabled<K extends keyof PopupTriggersSettings>(
  key: K,
  enabled: boolean,
): void {
  if (enabled) {
    patchTrigger(key, {
      triggerConditions: {
        ...createDefaultTriggerConditions(),
        enabled: true,
        delaySeconds: 5,
        pageViews: 3,
        operator: "and",
      },
    } as Partial<PopupTriggersSettings[K]>);
  } else {
    patchTrigger(key, {
      triggerConditions: {
        ...triggerSlotConditions(key),
        enabled: false,
      },
    } as Partial<PopupTriggersSettings[K]>);
  }
}

function patchFrequency(patch: {
  showAgain?: Partial<PopupFrequencyShowAgain>;
  stopShowing?: Partial<PopupFrequencyStopShowing>;
}): void {
  const cur = popup.value;
  const nextShowAgain = patch.showAgain
    ? { ...cur.frequency.showAgain, ...patch.showAgain }
    : cur.frequency.showAgain;
  const nextStop = patch.stopShowing
    ? { ...cur.frequency.stopShowing, ...patch.stopShowing }
    : cur.frequency.stopShowing;
  commit({
    ...cur,
    frequency: normalizePopupFrequencySettings({
      showAgain: nextShowAgain,
      stopShowing: nextStop,
    }),
  });
}

function setShowAgainMode(mode: PopupShowAgainMode): void {
  patchFrequency({ showAgain: { mode } });
}

function setIntervalValue(ev: Event): void {
  const raw = Number((ev.target as HTMLInputElement).value);
  patchFrequency({
    showAgain: {
      intervalValue: Number.isFinite(raw) ? Math.max(1, Math.floor(raw)) : 1,
    },
  });
}

function setIntervalUnit(unit: PopupFrequencyIntervalUnit): void {
  patchFrequency({ showAgain: { intervalUnit: unit } });
}

function setAfterSeenCount(ev: Event): void {
  const raw = Number((ev.target as HTMLInputElement).value);
  patchFrequency({
    stopShowing: {
      afterSeenCount: Number.isFinite(raw) ? Math.max(1, Math.floor(raw)) : 1,
    },
  });
}

function setAfterClosedBehavior(
  behavior: PopupStopShowingAfterClosedBehavior,
): void {
  patchFrequency({ stopShowing: { afterClosedBehavior: behavior } });
}

function onIntervalUnitSelect(ev: Event): void {
  const raw = (ev.target as HTMLSelectElement).value;
  if (raw === "minutes" || raw === "hours" || raw === "days") {
    setIntervalUnit(raw);
  }
}

function onAfterClosedSelect(ev: Event): void {
  const raw = (ev.target as HTMLSelectElement).value;
  if (raw === "never_display_again" || raw === "respect_show_again") {
    setAfterClosedBehavior(raw);
  }
}

const embedTriggersWrappedJson = computed(() =>
  JSON.stringify(
    { triggers: toPopupTriggersEmbedJson(popup.value.triggers) },
    null,
    2,
  ),
);

async function copyEmbedTriggersJson(): Promise<void> {
  try {
    await navigator.clipboard.writeText(embedTriggersWrappedJson.value);
    debugCopyDone.value = true;
    window.setTimeout(() => {
      debugCopyDone.value = false;
    }, 2000);
  } catch {
    debugCopyDone.value = false;
  }
}
</script>

<template>
  <div
    class="tpl-popup-display-rules tpl:mx-auto tpl:max-w-3xl tpl:px-6 tpl:py-8 tpl:pb-14 tpl:bg-[var(--tpl-bg)]"
  >
    <div
      class="tpl:mb-6 tpl:flex tpl:flex-wrap tpl:items-center tpl:justify-between tpl:gap-3"
    >
      <div
        class="tpl:flex tpl:flex-wrap tpl:gap-1 tpl:rounded-lg tpl:bg-[var(--tpl-bg-elevated)] tpl:p-1"
      >
        <button
          v-for="tab in rulesTabs"
          :key="tab.id"
          type="button"
          class="tpl:rounded-md tpl:border-none tpl:px-3 tpl:py-1.5 tpl:text-xs tpl:font-medium tpl:transition-colors"
          :class="
            rulesTab === tab.id
              ? 'tpl:bg-[var(--tpl-bg)] tpl:text-[var(--tpl-text)] tpl:shadow-sm'
              : 'tpl:bg-transparent tpl:text-[var(--tpl-text-muted)] hover:tpl:text-[var(--tpl-text)]'
          "
          @click="rulesTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>
      <!-- <button
        type="button"
        class="tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-transparent tpl:px-3 tpl:py-1.5 tpl:text-xs tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:hover:bg-[var(--tpl-bg-hover)]"
        @click="debugTriggersOpen = true"
      >
        {{ t.popupDisplayRules.debug }}
      </button> -->
    </div>

    <template v-if="rulesTab === 'frequency'">
      <h1
        class="tpl:mb-2 tpl:text-2xl tpl:font-semibold tpl:text-[var(--tpl-text)]"
      >
        {{ t.popupDisplayRules.frequencyHeading }}
      </h1>
      <p class="tpl:mb-12 tpl:text-sm tpl:text-[var(--tpl-text-muted)]">
        {{ t.popupDisplayRules.frequencyIntro }}
      </p>

      <section
        class="tpl:mb-14 tpl:grid tpl:grid-cols-[minmax(140px,11rem)_minmax(0,1fr)] tpl:gap-x-16 tpl:gap-y-4 tpl:items-start"
      >
        <h2
          class="tpl:m-0 tpl:pt-0.5 tpl:text-[15px] tpl:font-semibold tpl:text-[var(--tpl-text)]"
        >
          {{ t.popupDisplayRules.frequencyShowAgainTitle }}
        </h2>
        <div class="tpl:min-w-0">
          <p
            class="tpl:m-0 tpl:mb-4 tpl:text-sm tpl:text-[var(--tpl-text-muted)] tpl:leading-relaxed"
          >
            {{ t.popupDisplayRules.frequencyShowAgainDesc }}
          </p>
          <div
            class="tpl:flex tpl:flex-col tpl:gap-3"
            role="radiogroup"
            :aria-label="t.popupDisplayRules.frequencyShowAgainTitle"
          >
            <label
              class="tpl:flex tpl:cursor-pointer tpl:items-start tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
            >
              <input
                type="radio"
                name="tpl-popup-frequency-show-again"
                class="tpl:mt-1 tpl:size-4 tpl:accent-[var(--tpl-primary)] tpl:shrink-0"
                :checked="popup.frequency.showAgain.mode === 'page_load'"
                @change="setShowAgainMode('page_load')"
              />
              <span>{{ t.popupDisplayRules.frequencyEveryPageLoad }}</span>
            </label>
            <label
              class="tpl:flex tpl:cursor-pointer tpl:items-start tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
            >
              <input
                type="radio"
                name="tpl-popup-frequency-show-again"
                class="tpl:mt-1 tpl:size-4 tpl:accent-[var(--tpl-primary)] tpl:shrink-0"
                :checked="popup.frequency.showAgain.mode === 'session'"
                @change="setShowAgainMode('session')"
              />
              <span>{{ t.popupDisplayRules.frequencyEverySession }}</span>
            </label>
            <label
              class="tpl:flex tpl:flex-wrap tpl:cursor-pointer tpl:items-center tpl:gap-x-2 tpl:gap-y-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
            >
              <span class="tpl:flex tpl:items-start tpl:gap-2">
                <input
                  type="radio"
                  name="tpl-popup-frequency-show-again"
                  class="tpl:mt-1 tpl:size-4 tpl:accent-[var(--tpl-primary)] tpl:shrink-0"
                  :checked="popup.frequency.showAgain.mode === 'interval'"
                  @change="setShowAgainMode('interval')"
                />
                <span>{{ t.popupDisplayRules.frequencyEveryPrefix }}</span>
              </span>
              <input
                type="number"
                min="1"
                step="1"
                class="tpl:w-14 tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-2 tpl:py-1 tpl:text-sm tpl:tabular-nums disabled:tpl:cursor-not-allowed disabled:tpl:opacity-50"
                :disabled="popup.frequency.showAgain.mode !== 'interval'"
                :value="popup.frequency.showAgain.intervalValue"
                @change="setIntervalValue($event)"
              />
              <select
                class="tpl:min-w-[7rem] tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-2 tpl:py-1 tpl:text-sm disabled:tpl:cursor-not-allowed disabled:tpl:opacity-50"
                :disabled="popup.frequency.showAgain.mode !== 'interval'"
                :value="popup.frequency.showAgain.intervalUnit"
                @change="onIntervalUnitSelect($event)"
              >
                <option value="minutes">
                  {{ t.popupDisplayRules.frequencyIntervalUnitMinutes }}
                </option>
                <option value="hours">
                  {{ t.popupDisplayRules.frequencyIntervalUnitHours }}
                </option>
                <option value="days">
                  {{ t.popupDisplayRules.frequencyIntervalUnitDays }}
                </option>
              </select>
            </label>
          </div>
        </div>
      </section>

      <section
        class="tpl:grid tpl:grid-cols-[minmax(140px,11rem)_minmax(0,1fr)] tpl:gap-x-16 tpl:gap-y-4 tpl:items-start"
      >
        <h2
          class="tpl:m-0 tpl:pt-0.5 tpl:text-[15px] tpl:font-semibold tpl:text-[var(--tpl-text)]"
        >
          {{ t.popupDisplayRules.frequencyStopShowingTitle }}
        </h2>
        <div class="tpl:min-w-0">
          <p
            class="tpl:m-0 tpl:mb-4 tpl:text-sm tpl:text-[var(--tpl-text-muted)] tpl:leading-relaxed"
          >
            {{ t.popupDisplayRules.frequencyStopShowingDesc }}
          </p>
          <div class="tpl:flex tpl:flex-col tpl:gap-4">
            <label
              class="tpl:flex tpl:cursor-pointer tpl:items-start tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
            >
              <input
                type="checkbox"
                class="tpl:mt-1 tpl:size-4 tpl:accent-[var(--tpl-primary)] tpl:shrink-0 tpl:rounded"
                :checked="popup.frequency.stopShowing.afterSignupOrClick"
                @change="
                  patchFrequency({
                    stopShowing: {
                      afterSignupOrClick: ($event.target as HTMLInputElement)
                        .checked,
                    },
                  })
                "
              />
              <span>{{ t.popupDisplayRules.frequencyStopAfterSignup }}</span>
            </label>

            <label
              class="tpl:flex tpl:flex-wrap tpl:cursor-pointer tpl:items-center tpl:gap-x-2 tpl:gap-y-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
            >
              <span class="tpl:flex tpl:items-start tpl:gap-2">
                <input
                  type="checkbox"
                  class="tpl:mt-1 tpl:size-4 tpl:accent-[var(--tpl-primary)] tpl:shrink-0 tpl:rounded"
                  :checked="popup.frequency.stopShowing.afterSeenEnabled"
                  @change="
                    patchFrequency({
                      stopShowing: {
                        afterSeenEnabled: ($event.target as HTMLInputElement)
                          .checked,
                      },
                    })
                  "
                />
                <span>{{ t.popupDisplayRules.frequencyStopAfterSeen }}</span>
              </span>
              <input
                type="number"
                min="1"
                step="1"
                class="tpl:w-14 tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-2 tpl:py-1 tpl:text-sm tpl:tabular-nums disabled:tpl:cursor-not-allowed disabled:tpl:opacity-50"
                :disabled="!popup.frequency.stopShowing.afterSeenEnabled"
                :value="popup.frequency.stopShowing.afterSeenCount"
                @change="setAfterSeenCount($event)"
              />
              <span class="tpl:text-sm tpl:text-[var(--tpl-text)]">{{
                t.popupDisplayRules.frequencyStopTimesSuffix
              }}</span>
            </label>

            <div
              class="tpl:flex tpl:flex-wrap tpl:items-center tpl:gap-x-2 tpl:gap-y-2"
            >
              <label
                class="tpl:flex tpl:max-w-full tpl:cursor-pointer tpl:items-start tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
              >
                <input
                  type="checkbox"
                  class="tpl:mt-1 tpl:size-4 tpl:accent-[var(--tpl-primary)] tpl:shrink-0 tpl:rounded"
                  :checked="popup.frequency.stopShowing.afterClosedEnabled"
                  @change="
                    patchFrequency({
                      stopShowing: {
                        afterClosedEnabled: ($event.target as HTMLInputElement)
                          .checked,
                      },
                    })
                  "
                />
                <span>{{
                  t.popupDisplayRules.frequencyStopAfterClosedPrefix
                }}</span>
              </label>
              <select
                class="tpl:min-w-[12rem] tpl:max-w-full tpl:flex-1 tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-2 tpl:text-sm disabled:tpl:cursor-not-allowed disabled:tpl:opacity-50"
                :disabled="!popup.frequency.stopShowing.afterClosedEnabled"
                :value="popup.frequency.stopShowing.afterClosedBehavior"
                @change="onAfterClosedSelect($event)"
              >
                <option value="never_display_again">
                  {{ t.popupDisplayRules.frequencyClosedNeverAgain }}
                </option>
                <option value="respect_show_again">
                  {{ t.popupDisplayRules.frequencyClosedRespectRules }}
                </option>
              </select>
              <button
                type="button"
                class="tpl:inline-flex tpl:rounded tpl:border-none tpl:bg-transparent tpl:p-1 tpl:text-[var(--tpl-text-muted)] hover:tpl:text-[var(--tpl-text)]"
                :aria-label="t.popupDisplayRules.frequencyAfterClosedHelp"
                :title="t.popupDisplayRules.frequencyAfterClosedHelp"
              >
                <CircleHelp class="tpl:size-[18px]" :stroke-width="2" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </template>

    <template v-else>
      <h1
        class="tpl:mb-2 tpl:text-2xl tpl:font-semibold tpl:text-[var(--tpl-text)]"
      >
        {{ t.popupDisplayRules.triggerHeading }}
      </h1>
      <p class="tpl:mb-1 tpl:text-sm tpl:text-[var(--tpl-text-muted)]">
        {{ t.popupDisplayRules.triggerIntro }}
      </p>
      <p class="tpl:mb-8 tpl:text-sm tpl:text-[var(--tpl-text-muted)]">
        {{ t.popupDisplayRules.triggerIntroSecondary }}
        <a
          :href="hustlepopsHelpUrl.triggersOverview"
          target="_blank"
          rel="noopener noreferrer"
          class="tpl:text-[var(--tpl-primary)] tpl:underline tpl:underline-offset-2"
          >{{ t.popupDisplayRules.learnMore }}</a
        >
      </p>

      <div class="tpl:flex tpl:flex-col tpl:gap-4">
        <div
          v-for="def in triggerDefs"
          :key="def.key"
          class="tpl:rounded-xl tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:p-4 tpl:shadow-sm"
        >
          <div class="tpl:flex tpl:gap-4 tpl:items-start">
            <input
              type="checkbox"
              class="tpl:mt-1 tpl:h-4 tpl:w-4 tpl:accent-[var(--tpl-primary)] tpl:shrink-0 tpl:rounded"
              :checked="popup.triggers[def.key].enabled"
              @change="
                patchTrigger(def.key, {
                  enabled: ($event.target as HTMLInputElement).checked,
                })
              "
            />
            <component
              :is="def.icon"
              :size="22"
              :stroke-width="1.75"
              class="tpl:mt-0.5 tpl:shrink-0 tpl:text-[var(--tpl-text-muted)]"
            />
            <div class="tpl:min-w-0 tpl:flex-1">
              <h2
                class="tpl:text-[15px] tpl:font-semibold tpl:text-[var(--tpl-text)]"
              >
                {{ def.title }}
              </h2>
              <p
                class="tpl:mt-1 tpl:text-sm tpl:text-[var(--tpl-text-muted)] tpl:leading-relaxed"
              >
                {{ def.desc }}
                <template v-if="def.kind === 'exit'">
                  {{ " " }}
                  <a
                    :href="hustlepopsHelpUrl.exitIntent"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="tpl:text-[var(--tpl-primary)] tpl:underline tpl:underline-offset-2"
                    >{{ t.popupDisplayRules.learnMore }}</a
                  >
                </template>
              </p>

              <div
                v-if="popup.triggers[def.key].enabled && def.kind === 'landing'"
                class="tpl:mt-4 tpl:rounded-lg tpl:bg-[var(--tpl-bg-elevated)] tpl:px-3 tpl:py-3"
              >
                <label
                  class="tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="checkbox"
                    class="tpl:h-4 tpl:w-4 tpl:accent-[var(--tpl-primary)] tpl:rounded"
                    :checked="triggerSlotConditions('onLanding').enabled"
                    @change="
                      setConditionsPackEnabled(
                        'onLanding',
                        ($event.target as HTMLInputElement).checked,
                      )
                    "
                  />
                  {{ t.popupDisplayRules.addTriggerConditions }}
                </label>
                <PopupTriggerConditionDetails
                  v-if="triggerSlotConditions('onLanding').enabled"
                  :conditions="triggerSlotConditions('onLanding')"
                  @patch="(p) => patchConditions('onLanding', p)"
                />
              </div>

              <div
                v-if="popup.triggers[def.key].enabled && def.kind === 'exit'"
                class="tpl:mt-4 tpl:space-y-3 tpl:rounded-lg tpl:bg-[var(--tpl-bg-elevated)] tpl:px-3 tpl:py-3"
              >
                <label
                  class="tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="checkbox"
                    class="tpl:h-4 tpl:w-4 tpl:accent-[var(--tpl-primary)] tpl:rounded"
                    :checked="popup.triggers.onExit.triggerOnScrollUp === true"
                    @change="
                      patchTrigger('onExit', {
                        triggerOnScrollUp: ($event.target as HTMLInputElement)
                          .checked,
                      })
                    "
                  />
                  {{ t.popupDisplayRules.triggerOnScrollUpLabel }}
                </label>
                <label
                  class="tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="checkbox"
                    class="tpl:h-4 tpl:w-4 tpl:accent-[var(--tpl-primary)] tpl:rounded"
                    :checked="
                      popup.triggers.onExit.triggerOnBackButton === true
                    "
                    @change="
                      patchTrigger('onExit', {
                        triggerOnBackButton: ($event.target as HTMLInputElement)
                          .checked,
                      })
                    "
                  />
                  {{ t.popupDisplayRules.triggerOnBackButtonLabel }}
                </label>
                <hr class="tpl:border-[var(--tpl-border)]" />
                <label
                  class="tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="checkbox"
                    class="tpl:h-4 tpl:w-4 tpl:accent-[var(--tpl-primary)] tpl:rounded"
                    :checked="triggerSlotConditions('onExit').enabled"
                    @change="
                      setConditionsPackEnabled(
                        'onExit',
                        ($event.target as HTMLInputElement).checked,
                      )
                    "
                  />
                  {{ t.popupDisplayRules.addTriggerConditions }}
                </label>
                <PopupTriggerConditionDetails
                  v-if="triggerSlotConditions('onExit').enabled"
                  :conditions="triggerSlotConditions('onExit')"
                  @patch="(p) => patchConditions('onExit', p)"
                />
              </div>

              <div
                v-if="popup.triggers[def.key].enabled && def.kind === 'scroll'"
                class="tpl:mt-4 tpl:space-y-3 tpl:rounded-lg tpl:bg-[var(--tpl-bg-elevated)] tpl:px-3 tpl:py-3"
              >
                <label
                  class="tpl:flex tpl:flex-wrap tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  {{ t.popupDisplayRules.scrollShowAfter }}
                  <input
                    type="number"
                    min="0"
                    max="100"
                    class="tpl:w-16 tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-2 tpl:py-1 tpl:text-sm"
                    :value="popup.triggers.onScroll.scrollPercent ?? 50"
                    @change="
                      patchTrigger('onScroll', {
                        scrollPercent: Number(
                          ($event.target as HTMLInputElement).value,
                        ),
                      })
                    "
                  />
                  {{ t.popupDisplayRules.scrollPercentSuffix }}
                </label>
                <hr class="tpl:border-[var(--tpl-border)]" />
                <label
                  class="tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="checkbox"
                    class="tpl:h-4 tpl:w-4 tpl:accent-[var(--tpl-primary)] tpl:rounded"
                    :checked="triggerSlotConditions('onScroll').enabled"
                    @change="
                      setConditionsPackEnabled(
                        'onScroll',
                        ($event.target as HTMLInputElement).checked,
                      )
                    "
                  />
                  {{ t.popupDisplayRules.addTriggerConditions }}
                </label>
                <PopupTriggerConditionDetails
                  v-if="triggerSlotConditions('onScroll').enabled"
                  :conditions="triggerSlotConditions('onScroll')"
                  @patch="(p) => patchConditions('onScroll', p)"
                />
              </div>

              <div
                v-if="popup.triggers[def.key].enabled && def.kind === 'click'"
                class="tpl:mt-4 tpl:flex tpl:flex-col tpl:gap-3 tpl:rounded-lg tpl:bg-[var(--tpl-bg-elevated)] tpl:px-3 tpl:py-3"
              >
                <div
                  class="tpl:inline-flex tpl:rounded-full tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-1 tpl:text-sm tpl:font-mono tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="text"
                    class="tpl:w-36 tpl:border-none tpl:bg-transparent tpl:p-0 tpl:text-sm tpl:outline-none"
                    :value="popup.triggers.onClick.clickAnchor ?? ''"
                    @change="
                      patchTrigger('onClick', {
                        clickAnchor: (
                          $event.target as HTMLInputElement
                        ).value.trim(),
                      })
                    "
                  />
                </div>
                <p
                  class="tpl:text-xs tpl:leading-relaxed tpl:text-[var(--tpl-text-muted)]"
                >
                  {{ t.popupDisplayRules.clickHint }}
                </p>
                <a
                  :href="hustlepopsHelpUrl.clickTrigger"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="tpl:text-xs tpl:text-[var(--tpl-primary)] tpl:underline"
                  >{{ t.popupDisplayRules.learnMore }}</a
                >
                <hr class="tpl:border-[var(--tpl-border)]" />
                <label
                  class="tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="checkbox"
                    class="tpl:h-4 tpl:w-4 tpl:accent-[var(--tpl-primary)] tpl:rounded"
                    :checked="triggerSlotConditions('onClick').enabled"
                    @change="
                      setConditionsPackEnabled(
                        'onClick',
                        ($event.target as HTMLInputElement).checked,
                      )
                    "
                  />
                  {{ t.popupDisplayRules.addTriggerConditions }}
                </label>
                <PopupTriggerConditionDetails
                  v-if="triggerSlotConditions('onClick').enabled"
                  :conditions="triggerSlotConditions('onClick')"
                  @patch="(p) => patchConditions('onClick', p)"
                />
              </div>

              <div
                v-if="popup.triggers[def.key].enabled && def.kind === 'hover'"
                class="tpl:mt-4 tpl:flex tpl:flex-col tpl:gap-3 tpl:rounded-lg tpl:bg-[var(--tpl-bg-elevated)] tpl:px-3 tpl:py-3"
              >
                <div class="tpl:flex tpl:flex-wrap tpl:items-center tpl:gap-2">
                  <input
                    type="text"
                    class="tpl:min-w-[12rem] tpl:flex-1 tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-2 tpl:text-sm"
                    :placeholder="t.popupDisplayRules.hoverPlaceholder"
                    :value="popup.triggers.onHover.hoverElementId ?? ''"
                    @change="
                      patchTrigger('onHover', {
                        hoverElementId: (
                          $event.target as HTMLInputElement
                        ).value.trim(),
                      })
                    "
                  />
                  <a
                    :href="hustlepopsHelpUrl.hoverTrigger"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="tpl:text-sm tpl:text-[var(--tpl-primary)] tpl:underline"
                    >{{ t.popupDisplayRules.help }}</a
                  >
                </div>
                <hr class="tpl:border-[var(--tpl-border)]" />
                <label
                  class="tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="checkbox"
                    class="tpl:h-4 tpl:w-4 tpl:accent-[var(--tpl-primary)] tpl:rounded"
                    :checked="triggerSlotConditions('onHover').enabled"
                    @change="
                      setConditionsPackEnabled(
                        'onHover',
                        ($event.target as HTMLInputElement).checked,
                      )
                    "
                  />
                  {{ t.popupDisplayRules.addTriggerConditions }}
                </label>
                <PopupTriggerConditionDetails
                  v-if="triggerSlotConditions('onHover').enabled"
                  :conditions="triggerSlotConditions('onHover')"
                  @patch="(p) => patchConditions('onHover', p)"
                />
              </div>

              <div
                v-if="popup.triggers[def.key].enabled && def.kind === 'custom'"
                class="tpl:mt-4 tpl:rounded-lg tpl:bg-[var(--tpl-bg-elevated)] tpl:px-3 tpl:py-3"
              >
                <label
                  class="tpl:block tpl:text-xs tpl:font-medium tpl:text-[var(--tpl-text-muted)]"
                  >{{ t.popupDisplayRules.customEventLabel }}</label
                >
                <input
                  type="text"
                  class="tpl:mt-2 tpl:w-full tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-2 tpl:text-sm"
                  :placeholder="t.popupDisplayRules.customEventPlaceholder"
                  :value="popup.triggers.onCustomEvent.customEventName ?? ''"
                  @change="
                    patchTrigger('onCustomEvent', {
                      customEventName: (
                        $event.target as HTMLInputElement
                      ).value.trim(),
                    })
                  "
                />
                <hr class="tpl:mt-3 tpl:border-[var(--tpl-border)]" />
                <label
                  class="tpl:mt-3 tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="checkbox"
                    class="tpl:h-4 tpl:w-4 tpl:accent-[var(--tpl-primary)] tpl:rounded"
                    :checked="triggerSlotConditions('onCustomEvent').enabled"
                    @change="
                      setConditionsPackEnabled(
                        'onCustomEvent',
                        ($event.target as HTMLInputElement).checked,
                      )
                    "
                  />
                  {{ t.popupDisplayRules.addTriggerConditions }}
                </label>
                <PopupTriggerConditionDetails
                  v-if="triggerSlotConditions('onCustomEvent').enabled"
                  :conditions="triggerSlotConditions('onCustomEvent')"
                  @patch="(p) => patchConditions('onCustomEvent', p)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>

  <Teleport to="body">
    <div
      v-if="debugTriggersOpen"
      class="tpl:fixed tpl:inset-0 tpl:z-[1000] tpl:flex tpl:items-center tpl:justify-center tpl:bg-black/50 tpl:p-4"
      role="dialog"
      aria-modal="true"
      :aria-label="t.popupDisplayRules.debugJsonTitle"
      @click.self="debugTriggersOpen = false"
    >
      <div
        class="tpl:flex tpl:max-h-[85vh] tpl:w-full tpl:max-w-2xl tpl:flex-col tpl:overflow-hidden tpl:rounded-xl tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:shadow-xl"
      >
        <div
          class="tpl:flex tpl:flex-wrap tpl:items-center tpl:justify-between tpl:gap-2 tpl:border-b tpl:border-[var(--tpl-border)] tpl:px-4 tpl:py-3"
        >
          <h2 class="tpl:text-sm tpl:font-semibold tpl:text-[var(--tpl-text)]">
            {{ t.popupDisplayRules.debugJsonTitle }}
          </h2>
          <div class="tpl:flex tpl:items-center tpl:gap-2">
            <button
              type="button"
              class="tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-elevated)] tpl:px-3 tpl:py-1.5 tpl:text-xs tpl:font-medium tpl:text-[var(--tpl-text)] tpl:hover:bg-[var(--tpl-bg-hover)]"
              @click="copyEmbedTriggersJson"
            >
              {{
                debugCopyDone
                  ? t.popupDisplayRules.copyJsonDone
                  : t.popupDisplayRules.copyJson
              }}
            </button>
            <button
              type="button"
              class="tpl:rounded-md tpl:border tpl:border-[var(--tpl-border)] tpl:bg-transparent tpl:px-3 tpl:py-1.5 tpl:text-xs tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:hover:bg-[var(--tpl-bg-hover)]"
              @click="debugTriggersOpen = false"
            >
              {{ t.popupDisplayRules.debugJsonClose }}
            </button>
          </div>
        </div>
        <pre
          class="tpl:m-0 tpl:flex-1 tpl:overflow-auto tpl:p-4 tpl:text-left tpl:font-mono tpl:text-[11px] tpl:leading-relaxed tpl:text-[var(--tpl-text)]"
          >{{ embedTriggersWrappedJson }}</pre
        >
        <p
          class="tpl:m-0 tpl:border-t tpl:border-[var(--tpl-border)] tpl:px-4 tpl:py-2 tpl:text-xs tpl:text-[var(--tpl-text-muted)]"
        >
          {{ t.popupDisplayRules.debugJsonHint }}
        </p>
      </div>
    </div>
  </Teleport>
</template>
